import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'shared/web';
import type { EagColumn } from '../../../../../../../../types/EagColumn';
import type { EagFilterProps } from '../../../../../../../../types/EagFilterProps';
import type { FilterState } from '../../../../../../../../types/FilterState';
import { renderFilter } from '../../../../../../../../utils/renderFilter';
const FILTER_DEBOUNCE_DELAY_MS = 250;
const FILTER_DEBOUNCE_MAX_WAIT_MS = 2000;
// Stable empty reference — prevents `?? {}` from creating a new object every render
// which would cause the sync useEffect to fire on every render cycle (React Compiler violation).
const EMPTY_FILTER = {} as FilterState;
type ColumnMenuFilterTabProps = {
  column: EagColumn;
  filterState: FilterState;
  onFilterChange: (key: string, value: unknown) => void;
};

/**
 * Filter tab content for the column header menu.
 *
 * Renders the same filter widget used in FilterPanel and FilterChipPopover,
 * providing a consistent inline editing experience directly from the column header.
 *
 * Uses local state for immediate input responsiveness, with debounced propagation
 * (250ms delay, 2s max wait) to the parent store to avoid a network request on
 * every keystroke while still flushing during continuous typing.
 */
export function ColumnMenuFilterTab(props) {
  const $ = _c(16);
  const storeValue = (props.filterState[props.column.key] ?? EMPTY_FILTER) as FilterState;
  const [localValue, setLocalValue] = useState(storeValue);
  let t0;
  let t1;
  if ($[0] !== storeValue) {
    t0 = () => {
      setLocalValue(storeValue);
    };
    t1 = [storeValue];
    $[0] = storeValue;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  const debouncedOnFilterChange = useDebouncedCallback(props.onFilterChange, FILTER_DEBOUNCE_DELAY_MS, FILTER_DEBOUNCE_MAX_WAIT_MS);
  let t2;
  if ($[3] !== debouncedOnFilterChange || $[4] !== props.column.key) {
    t2 = value => {
      setLocalValue(value);
      debouncedOnFilterChange(props.column.key, value);
    };
    $[3] = debouncedOnFilterChange;
    $[4] = props.column.key;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleChange = t2;
  const t3 = props.column;
  let t4;
  let t5;
  let t6;
  if ($[6] !== handleChange || $[7] !== localValue || $[8] !== props.column) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const filterProps = {
        value: localValue,
        onChange: handleChange,
        isDisabled: false,
        column: t3
      };
      if (!props.column.filter) {
        let t7;
        if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
          t7 = <div className="p-4 text-xs text-gray-400">No filter available</div>;
          $[12] = t7;
        } else {
          t7 = $[12];
        }
        t6 = t7;
        break bb0;
      }
      t4 = "p-4";
      t5 = renderFilter(props.column, filterProps);
    }
    $[6] = handleChange;
    $[7] = localValue;
    $[8] = props.column;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[13] !== t4 || $[14] !== t5) {
    t7 = <div className={t4}>{t5}</div>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}